import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@h2/ui';
import { Button, Input, Badge, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@h2/ui';
import { Search, Plus, Users, UserCheck, UserX, MoreHorizontal, Edit, MessageCircle, Trash2, X } from 'lucide-react';
import { useWorkerGroupStore, WorkerGroup } from '../stores/useWorkerGroupStore';
import { useWorkerStore } from '../stores/useWorkerStore';
import { WorkerGroupFormModal } from '../components/worker/WorkerGroupFormModal';
import { DeleteConfirmModal } from '../components/worker/DeleteConfirmModal';
import { AdvancedSearchInput, SearchHelpItem } from '../components/search/AdvancedSearchInput';
import { useSearchParams } from '../hooks/useSearchParams';

const WorkerGroupManagementPage: React.FC = () => {
  const {
    groups,
    isLoading: groupsLoading,
    searchQuery: groupSearchQuery,
    statusFilter: groupStatusFilter,
    setSearchQuery: setGroupSearchQuery,
    setStatusFilter: setGroupStatusFilter,
    loadGroups,
    createGroup,
    updateGroup,
    deleteGroup,
    getFilteredGroups
  } = useWorkerGroupStore();

  const { workers, loadWorkers } = useWorkerStore();

  const [showGroupForm, setShowGroupForm] = useState(false);
  const [editingGroup, setEditingGroup] = useState<WorkerGroup | null>(null);
  const [deletingGroup, setDeletingGroup] = useState<WorkerGroup | null>(null);

  // URL 검색 파라미터 관리
  const { 
    searchState, 
    updateSearchState, 
    clearSearchState, 
    shareSearchState,
    hasActiveSearch 
  } = useSearchParams({
    groupQuery: '',
    groupStatus: 'all'
  });

  useEffect(() => {
    loadGroups();
    loadWorkers();
  }, [loadGroups, loadWorkers]);

  // URL 파라미터와 로컬 상태 동기화
  useEffect(() => {
    if (searchState.groupQuery !== undefined) {
      setGroupSearchQuery(searchState.groupQuery);
    }
    if (searchState.groupStatus !== undefined) {
      setGroupStatusFilter(searchState.groupStatus as 'all' | 'active' | 'inactive');
    }
  }, [searchState]);

  const filteredGroups = getFilteredGroups();

  // 검색 도움말 항목
  const groupSearchHelp: SearchHelpItem[] = [
    {
      key: '그룹명',
      description: '그룹 이름으로 검색',
      example: '그룹명:번역팀'
    },
    {
      key: '전문분야',
      description: '전문분야로 검색 (부분일치)',
      example: '전문분야:번역'
    },
    {
      key: '설명',
      description: '그룹 설명으로 검색',
      example: '설명:"전문 번역팀"'
    },
    {
      key: '상태',
      description: '그룹 상태로 검색',
      example: '상태:active'
    }
  ];

  const handleEditGroup = (group: WorkerGroup) => {
    setEditingGroup(group);
    setShowGroupForm(true);
  };

  const handleDeleteGroup = async () => {
    if (deletingGroup) {
      await deleteGroup(deletingGroup.id);
      setDeletingGroup(null);
    }
  };

  const handleAction = (group: WorkerGroup, action: string) => {
    switch (action) {
      case 'edit':
        handleEditGroup(group);
        break;
      case 'deactivate':
        console.log('비활성화:', group.name);
        break;
      case 'add-member':
        console.log('멤버 추가:', group.name);
        break;
      case 'chat':
        console.log('채팅:', group.name);
        break;
      case 'delete':
        setDeletingGroup(group);
        break;
    }
  };

  const getWorkerById = (id: string) => {
    return workers.find(worker => worker.id === id);
  };


  const getMemberNames = (memberIds: string[]) => {
    return memberIds.map(id => {
      const member = getWorkerById(id);
      return member ? member.name : '알 수 없음';
    }).join(', ');
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* 헤더 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">작업자 그룹 관리</h1>
          <p className="text-muted-foreground">
            작업자 그룹을 관리합니다
          </p>
        </div>
      </div>

      {/* 검색 및 필터 */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <AdvancedSearchInput
            value={groupSearchQuery}
            onChange={(query) => {
              setGroupSearchQuery(query);
              updateSearchState({ groupQuery: query });
            }}
            placeholder="그룹명 전문분야:'전문 번역' - 그룹 검색"
            helpItems={groupSearchHelp}
            className="w-96"
            onShare={(url) => {
              navigator.clipboard.writeText(shareSearchState({ 
                ...searchState, 
                groupQuery: groupSearchQuery,
                groupStatus: groupStatusFilter
              }));
            }}
          />
          <div className="flex space-x-2">
            <Button
              variant={groupStatusFilter === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => {
                setGroupStatusFilter('all');
                updateSearchState({ groupStatus: 'all' });
              }}
            >
              전체
            </Button>
            <Button
              variant={groupStatusFilter === 'active' ? 'default' : 'outline'}
              size="sm"
              onClick={() => {
                setGroupStatusFilter('active');
                updateSearchState({ groupStatus: 'active' });
              }}
            >
              <UserCheck className="h-4 w-4 mr-1" />
              활성
            </Button>
            <Button
              variant={groupStatusFilter === 'inactive' ? 'default' : 'outline'}
              size="sm"
              onClick={() => {
                setGroupStatusFilter('inactive');
                updateSearchState({ groupStatus: 'inactive' });
              }}
            >
              <UserX className="h-4 w-4 mr-1" />
              비활성
            </Button>
          </div>
        </div>
        
        <div className="flex space-x-2">
          {hasActiveSearch && (
            <Button variant="outline" onClick={() => {
              setGroupSearchQuery('');
              setGroupStatusFilter('all');
              clearSearchState();
            }}>
              <X className="h-4 w-4 mr-2" />
              검색 초기화
            </Button>
          )}
          <Button onClick={() => setShowGroupForm(true)}>
            <Plus className="h-4 w-4 mr-2" />
            새 그룹
          </Button>
        </div>
      </div>

      {/* 그룹 목록 */}
      {groupsLoading ? (
        <div className="text-center py-8 text-muted-foreground">
          그룹 목록을 불러오는 중...
        </div>
      ) : filteredGroups.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredGroups.map((group) => (
            <Card key={group.id} className="group relative hover:shadow-lg transition-shadow h-fit">
              <CardHeader className="pb-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <Users className="h-5 w-5 text-blue-600 flex-shrink-0" />
                      <CardTitle className="text-lg truncate">{group.name}</CardTitle>
                      <Badge 
                        variant={group.status === 'active' ? 'default' : 'secondary'}
                        className="flex-shrink-0"
                      >
                        {group.status === 'active' ? '활성' : '비활성'}
                      </Badge>
                    </div>
                    <CardDescription className="text-sm line-clamp-2">
                      {group.description}
                    </CardDescription>
                  </div>
                  
                  {/* 액션 드롭다운 */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 ml-2"
                      >
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleAction(group, 'edit')}>
                        <Edit className="h-4 w-4 mr-2" />
                        수정
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleAction(group, 'deactivate')}>
                        <UserX className="h-4 w-4 mr-2" />
                        비활성화
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => handleAction(group, 'add-member')}>
                        <Plus className="h-4 w-4 mr-2" />
                        멤버 추가
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleAction(group, 'chat')}>
                        <MessageCircle className="h-4 w-4 mr-2" />
                        채팅
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem 
                        onClick={() => handleAction(group, 'delete')}
                        className="text-destructive"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        삭제
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardHeader>
              
              <CardContent className="pt-0 space-y-4">
                <div>
                  <div className="text-sm font-medium text-muted-foreground mb-2">전문분야</div>
                  <div className="flex flex-wrap gap-1">
                    {group.specialties.map((specialty, idx) => (
                      <Badge key={idx} variant="outline" className="text-xs">
                        {specialty}
                      </Badge>
                    ))}
                  </div>
                </div>
                
                <div>
                  <div className="text-sm font-medium text-muted-foreground mb-1">
                    멤버 ({group.members.length}명)
                  </div>
                  <div className="text-sm text-muted-foreground line-clamp-2" title={getMemberNames(group.members)}>
                    {getMemberNames(group.members) || '멤버 없음'}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 text-muted-foreground">
          {groupSearchQuery || groupStatusFilter !== 'all' 
            ? '검색 조건에 맞는 그룹이 없습니다.' 
            : '등록된 그룹이 없습니다.'}
        </div>
      )}

      {/* 그룹 생성/수정 모달 */}
      {showGroupForm && (
        <WorkerGroupFormModal
          group={editingGroup}
          onClose={() => {
            setShowGroupForm(false);
            setEditingGroup(null);
          }}
        />
      )}

      {/* 삭제 확인 모달 */}
      {deletingGroup && (
        <DeleteConfirmModal
          title="그룹 삭제"
          message={`'${deletingGroup.name}' 그룹을 삭제하시겠습니까?`}
          onConfirm={handleDeleteGroup}
          onCancel={() => setDeletingGroup(null)}
        />
      )}
    </div>
  );
};

export default WorkerGroupManagementPage;
